// src/components/tickets/detail/StructuredFieldsPanel.tsx
//
// The structured-fields panel (Story 4.2 AC 3 read view + Story 4.5 AC 3/5 edit).
// Renders the four canonical keys (Brand / Region / Channel / Payer — region
// resolved to its yak_regions name) plus the per-type `extras`, with human labels
// from fieldDisplay.ts (parity-tested against the AD-14 single source). Each
// attribute named in wildcard_attributes shows the muted "not captured on call"
// marker. Each corrected field shows a history icon opening its append-only
// old → new trail (from yak_field_corrections).
//
// Story 4.5 fills 4.2's seam: the CURRENT ASSIGNEE gets a quiet per-field edit
// (Pencil) / Add affordance and an inline editor (text, or a region picker over
// the org tree). Saving records an append-only correction via tx_correct_field
// (no re-assignment — FR-37); the panel re-renders from the server's answer (the
// parent refetches). Editing a routing attribute states, in place, that
// corrections don't change assignment and links to Reassign. Corrections are
// legal on OPEN, ANSWERED, and CLOSED tickets (record hygiene, not work).

import { useState, useEffect, useRef } from 'react'
import { History, Pencil } from 'lucide-react'
import { fieldLabel, wildcardFieldKey } from '../../../utils/fieldDisplay'
import {
  editableFieldPathsForType,
  isRoutingAttribute,
  currentFieldValue,
} from '../../../utils/fieldEditors'
import { buildRegionTree, flattenRegionTree } from '../../../utils/regionTree'
import { personName } from '../../../utils/ticketDisplay'
import { formatDateTime } from '../../../utils/dateUtils'
import type {
  YakTicketDetail,
  YakCorrectionWithAuthor,
  YakRegion,
} from '../../../types/yak'

const ROUTING_NOTE =
  "Corrections don't change who this ticket is assigned to — use Reassign to move it."

interface FieldRow {
  // React key + the yak_field_corrections `field` path (4.2's dotted convention).
  path: string
  label: string
  // Display value (region resolved to a name).
  value: string | null
  // Stored value for prefilling the editor (region → the raw uuid).
  rawValue: string | null
  wildcard: boolean
  note: string | null
  // Assignee-correctable? (unknown/legacy extras keys render read-only).
  editable: boolean
  isRegion: boolean
}

function str(v: unknown): string | null {
  return typeof v === 'string' && v.trim() ? v : null
}

function buildRows(detail: YakTicketDetail): FieldRow[] {
  const sf = detail.ticket.structured_fields ?? {}
  const wildcards = new Set(
    detail.ticket.wildcard_attributes.map(wildcardFieldKey)
  )
  const allowedPaths = editableFieldPathsForType(detail.ticket.question_type)
  const rows: FieldRow[] = []

  for (const path of allowedPaths) {
    const isRegion = path === 'region_id'
    const raw = currentFieldValue(sf, path)
    // The canonical rows label + wildcard on the matcher vocabulary (region_id →
    // region); extras paths label on their key.
    const isRouting = isRoutingAttribute(path)
    const labelKey = isRouting
      ? wildcardFieldKey(path)
      : path.slice('extras.'.length)

    let value = raw
    let note: string | null = null
    if (isRegion) {
      // Current region display keeps 4.2's resolved-name path (detail.regionName);
      // an unresolvable id shows raw + note.
      value = raw ? (detail.regionName ?? raw) : null
      note = raw && !detail.regionName ? 'region no longer exists' : null
    }

    rows.push({
      path,
      label: fieldLabel(labelKey),
      value,
      rawValue: raw,
      wildcard: isRouting ? wildcards.has(labelKey) : false,
      note,
      editable: true,
      isRegion,
    })
  }

  // Unknown / legacy extras keys present on the ticket but NOT in the type's
  // frozen interior render READ-ONLY via the humanized fallback (no edit
  // affordance) — forward-compatible with drift.
  const extras = sf['extras']
  if (extras && typeof extras === 'object' && !Array.isArray(extras)) {
    const allowedExtraKeys = new Set(
      allowedPaths
        .filter(p => p.startsWith('extras.'))
        .map(p => p.slice('extras.'.length))
    )
    for (const [k, v] of Object.entries(extras as Record<string, unknown>)) {
      if (allowedExtraKeys.has(k)) continue
      rows.push({
        path: `extras.${k}`,
        label: fieldLabel(k),
        value: str(v),
        rawValue: str(v),
        wildcard: false,
        note: null,
        editable: false,
        isRegion: false,
      })
    }
  }

  return rows
}

// Resolve a region uuid to its name for the trail (full org list), raw + note on
// a miss (4.2's rule, now applied to trail entries too).
function resolveRegionDisplay(
  value: string | null,
  regionNameById: Map<string, string>
): { text: string; missing: boolean } | null {
  if (value === null) return null
  const name = regionNameById.get(value)
  return name ? { text: name, missing: false } : { text: value, missing: true }
}

function CorrectionTrail({
  corrections,
  isRegionField,
  regionNameById,
}: {
  corrections: YakCorrectionWithAuthor[]
  isRegionField: boolean
  regionNameById: Map<string, string>
}) {
  const renderValue = (v: string | null, cleared: boolean) => {
    if (v === null) return <span>{cleared ? '(cleared)' : '(none)'}</span>
    if (isRegionField) {
      const r = resolveRegionDisplay(v, regionNameById)
      if (r) {
        return (
          <span>
            {r.text}
            {r.missing ? (
              <span className="text-gray-400"> (region no longer exists)</span>
            ) : null}
          </span>
        )
      }
    }
    return <span>{v}</span>
  }
  return (
    <ol className="mt-1 space-y-1 rounded-md bg-gray-50 p-2 text-xs text-gray-600">
      {corrections.map(c => (
        <li key={c.id}>
          <span className="text-gray-900">
            {renderValue(c.old_value, false)}
          </span>
          {' → '}
          <span className="text-gray-900">
            {renderValue(c.new_value, true)}
          </span>
          <span className="text-gray-500">
            {' '}
            · {c.author ? personName(c.author) : 'Unknown'}
            {c.created_at ? ` · ${formatDateTime(c.created_at)}` : ''}
          </span>
        </li>
      ))}
    </ol>
  )
}

function FieldEditor({
  row,
  regions,
  pending,
  isClosed,
  onSave,
  onCancel,
  onOpenReassign,
}: {
  row: FieldRow
  regions: YakRegion[]
  pending: boolean
  isClosed: boolean
  onSave: (value: string | null) => Promise<void>
  onCancel: () => void
  onOpenReassign?: () => void
}) {
  const [draft, setDraft] = useState(row.rawValue ?? '')
  const [error, setError] = useState<string | null>(null)
  const inputRef = useRef<HTMLInputElement | HTMLSelectElement | null>(null)
  const errorId = `field-error-${row.path}`

  useEffect(() => {
    inputRef.current?.focus()
  }, [])

  const regionOptions = row.isRegion
    ? flattenRegionTree(buildRegionTree(regions))
    : []

  // Submit the normalized value: blank / "No region" ⇒ clear (null).
  const submit = async (raw: string) => {
    const value = raw.trim() === '' ? null : raw
    setError(null)
    try {
      await onSave(value)
      // success: the parent closes this editor (re-render from the server).
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Correction failed.')
    }
  }

  return (
    <div className="mt-1 space-y-2">
      <div className="flex items-center gap-2">
        {row.isRegion ? (
          <select
            ref={el => {
              inputRef.current = el
            }}
            value={draft}
            disabled={pending}
            aria-label={`Region for this ticket`}
            aria-describedby={error ? errorId : undefined}
            onChange={e => setDraft(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter') {
                e.preventDefault()
                void submit(draft)
              } else if (e.key === 'Escape') {
                onCancel()
              }
            }}
            className="min-w-0 flex-1 rounded-md border border-gray-300 px-2 py-1 text-sm"
          >
            <option value="">No region</option>
            {regionOptions.map(({ region, depth }) => (
              <option key={region.id} value={region.id}>
                {`${'  '.repeat(depth)}${region.name}`}
              </option>
            ))}
          </select>
        ) : (
          <input
            ref={el => {
              inputRef.current = el
            }}
            type="text"
            value={draft}
            disabled={pending}
            aria-label={`${row.label} value`}
            aria-describedby={error ? errorId : undefined}
            onChange={e => setDraft(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter') {
                e.preventDefault()
                void submit(draft)
              } else if (e.key === 'Escape') {
                onCancel()
              }
            }}
            className="min-w-0 flex-1 rounded-md border border-gray-300 px-2 py-1 text-sm"
          />
        )}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={() => void submit(draft)}
          disabled={pending}
          className="btn-outline"
        >
          {pending ? 'Saving…' : 'Save'}
        </button>
        <button
          type="button"
          onClick={onCancel}
          disabled={pending}
          className="btn-outline"
        >
          Cancel
        </button>
        {/* Clear a non-empty existing value (no confirm — the trail is the safety
            net; a mistaken clear is remedied by another correction). */}
        {row.rawValue !== null && !row.isRegion ? (
          <button
            type="button"
            onClick={() => void submit('')}
            disabled={pending}
            className="text-xs text-gray-500 hover:text-gray-700"
          >
            Clear
          </button>
        ) : null}
      </div>

      {/* Routing-attribute note (AC 3): corrections never re-run assignment. The
          Reassign action opens 4.4's dialog; omitted on a closed ticket. */}
      {isRoutingAttribute(row.path) ? (
        <p className="text-xs text-gray-500">
          {ROUTING_NOTE}
          {!isClosed && onOpenReassign ? (
            <>
              {' '}
              <button
                type="button"
                onClick={onOpenReassign}
                className="font-medium text-brand-600 hover:text-brand-700 hover:underline"
              >
                Reassign
              </button>
            </>
          ) : null}
        </p>
      ) : null}

      {error ? (
        <div
          id={errorId}
          role="alert"
          className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700"
        >
          {error}
        </div>
      ) : null}
    </div>
  )
}

export function StructuredFieldsPanel({
  detail,
  currentUserId = null,
  correctingField = null,
  onCorrect,
  onOpenReassign,
}: {
  detail: YakTicketDetail
  currentUserId?: string | null
  correctingField?: string | null
  onCorrect?: (field: string, value: string | null) => Promise<void>
  onOpenReassign?: () => void
}) {
  const [openTrail, setOpenTrail] = useState<string | null>(null)
  const [editingPath, setEditingPath] = useState<string | null>(null)
  const editTriggerRef = useRef<HTMLButtonElement | null>(null)
  const rows = buildRows(detail)

  const isClosed = detail.ticket.status === 'closed'
  // Assignee-only affordances (cosmetic — the server enforces not_assignee).
  const isAssignee =
    currentUserId !== null && detail.ticket.assignee_user_id === currentUserId
  const canEdit = isAssignee && typeof onCorrect === 'function'

  const regionNameById = new Map<string, string>(
    detail.regions.map(r => [r.id, r.name])
  )

  const correctionsByField = new Map<string, YakCorrectionWithAuthor[]>()
  for (const c of detail.corrections) {
    const list = correctionsByField.get(c.field) ?? []
    list.push(c)
    correctionsByField.set(c.field, list)
  }

  // Panel-level save handler: success closes the editor (the parent's refetch
  // re-renders the row); a business error rethrows for the editor's inline copy.
  const handleSave = async (path: string, value: string | null) => {
    if (!onCorrect) return
    await onCorrect(path, value)
    setEditingPath(null)
  }

  const closeEditor = () => {
    setEditingPath(null)
    editTriggerRef.current?.focus()
  }

  return (
    <section className="card p-5" aria-labelledby="fields-heading">
      <h2
        id="fields-heading"
        className="mb-3 text-sm font-semibold uppercase tracking-wide text-gray-500"
      >
        Structured fields
      </h2>

      <dl className="space-y-3">
        {rows.map(row => {
          const trail = correctionsByField.get(row.path) ?? []
          const hasTrail = trail.length > 0
          const isTrailOpen = openTrail === row.path
          const isEditing = editingPath === row.path
          const showAffordance = canEdit && row.editable
          return (
            <div key={row.path}>
              <div className="flex items-baseline gap-2">
                <dt className="w-28 shrink-0 text-sm font-medium text-gray-500">
                  {row.label}
                </dt>
                <dd className="flex min-w-0 flex-1 items-baseline gap-2 text-sm text-gray-900">
                  {row.value ? (
                    <span className="break-words">{row.value}</span>
                  ) : row.wildcard ? (
                    <span className="text-gray-400">not captured on call</span>
                  ) : (
                    <span className="text-gray-400">—</span>
                  )}
                  {row.note ? (
                    <span className="text-xs text-gray-400">({row.note})</span>
                  ) : null}
                  {hasTrail ? (
                    <button
                      type="button"
                      onClick={() =>
                        setOpenTrail(isTrailOpen ? null : row.path)
                      }
                      className="text-gray-400 hover:text-gray-600"
                      aria-label={`Show correction history for ${row.label}`}
                      aria-expanded={isTrailOpen}
                    >
                      <History className="h-4 w-4" aria-hidden="true" />
                    </button>
                  ) : null}
                  {showAffordance && !isEditing ? (
                    <button
                      type="button"
                      onClick={e => {
                        editTriggerRef.current = e.currentTarget
                        setEditingPath(row.path)
                      }}
                      className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700"
                      aria-label={`${row.value ? 'Edit' : 'Add'} ${row.label}`}
                    >
                      <Pencil className="h-3.5 w-3.5" aria-hidden="true" />
                      {row.value ? 'Edit' : 'Add'}
                    </button>
                  ) : null}
                </dd>
              </div>

              {isEditing && onCorrect ? (
                <FieldEditor
                  row={row}
                  regions={detail.regions}
                  pending={correctingField === row.path}
                  isClosed={isClosed}
                  onSave={value => handleSave(row.path, value)}
                  onCancel={closeEditor}
                  onOpenReassign={onOpenReassign}
                />
              ) : null}

              {hasTrail && isTrailOpen ? (
                <CorrectionTrail
                  corrections={trail}
                  isRegionField={row.path === 'region_id'}
                  regionNameById={regionNameById}
                />
              ) : null}
            </div>
          )
        })}
      </dl>
    </section>
  )
}
